import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, Crown, Copy, Plus, Truck, Trash2, DollarSign, Route, Package,
  Loader2, LogOut, KeyRound, Eye, X, ShieldCheck, Mail, MessageSquare, Send, Trophy,
  AlertTriangle, FileDown, Ticket,
} from "lucide-react";
import { api, BACKEND_BASE, downloadPdf } from "@/lib/api";
import { fmtMoney } from "@/lib/theme";
import { useAuth } from "@/auth/AuthContext";
import { useProStatus } from "@/hooks/useProStatus";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { TrucksPanel } from "@/components/fleet/TrucksPanel";
import { ComparePanel, RangeToggle } from "@/components/fleet/ComparePanel";

const inputCls = "mt-1.5 h-11 border-rig-borderStrong bg-rig-bg text-white placeholder:text-rig-mute";

function StatBox({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-rig-border bg-rig-surface p-4">
      <div className="flex items-center gap-2 text-rig-dim">
        <Icon className="h-4 w-4" />
        <span className="font-display text-[10px] font-bold uppercase tracking-[0.18em]">{label}</span>
      </div>
      <div className={`mt-1.5 font-display text-2xl font-bold ${accent ? "text-rig-accent" : "text-white"}`}>{value}</div>
    </div>
  );
}

/* ---------------- No fleet yet ---------------- */
function NoFleet({ isPro, onCreated, onJoined }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);

  const create = async () => {
    setCreating(true);
    try {
      await api("/fleet", { method: "POST", body: { name } });
      toast.success("Fleet created");
      onCreated();
    } catch (e) {
      toast.error(e?.message || "Could not create fleet");
    } finally {
      setCreating(false);
    }
  };

  const join = async () => {
    setJoining(true);
    try {
      await api("/fleet/join", { method: "POST", body: { join_code: code } });
      toast.success("Joined fleet");
      onJoined();
    } catch (e) {
      toast.error(e?.message || "Could not join fleet");
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-2xl border border-rig-accent/50 bg-rig-surface p-6" data-testid="create-fleet-card">
        <div className="flex items-center gap-2">
          <Crown className="h-5 w-5 text-rig-accent" />
          <h2 className="font-display text-lg font-bold uppercase tracking-wide text-white">Start a Fleet</h2>
        </div>
        <p className="mt-2 text-sm text-rig-dim">
          Track multiple trucks in one place. Every driver&apos;s receipts, hauls and miles roll up to your dispatcher dashboard.
        </p>
        {isPro ? (
          <>
            <Label className="mt-5 block text-sm text-rig-dim">Fleet name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Smith Trucking"
              className={inputCls}
              data-testid="fleet-name-input"
            />
            <Button
              onClick={create}
              disabled={creating || !name.trim()}
              data-testid="create-fleet-btn"
              className="mt-4 h-11 w-full bg-rig-accent font-display text-sm font-bold uppercase tracking-widest text-rig-accentText hover:bg-rig-accentDark"
            >
              {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
              Create Fleet
            </Button>
          </>
        ) : (
          <div className="mt-5 rounded-xl border border-rig-border bg-rig-surface2 p-4">
            <p className="text-sm text-white">Fleet management is a <span className="text-rig-accent">Pro</span> feature.</p>
            <Button
              onClick={() => navigate("/upgrade")}
              data-testid="fleet-upgrade-btn"
              className="mt-3 h-10 w-full bg-rig-accent font-display text-xs font-bold uppercase tracking-widest text-rig-accentText hover:bg-rig-accentDark"
            >
              Upgrade to Pro
            </Button>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-rig-border bg-rig-surface p-6" data-testid="join-fleet-card">
        <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-rig-blue" />
          <h2 className="font-display text-lg font-bold uppercase tracking-wide text-white">Join a Fleet</h2>
        </div>
        <p className="mt-2 text-sm text-rig-dim">
          Got a join code from your owner-operator? Enter it to link your account to their fleet.
        </p>
        <Label className="mt-5 block text-sm text-rig-dim">Join code</Label>
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="RIG-ABCDE"
          className={`${inputCls} font-display tracking-widest`}
          data-testid="join-code-input"
        />
        <Button
          onClick={join}
          disabled={joining || code.trim().length < 5}
          data-testid="join-fleet-btn"
          variant="outline"
          className="mt-4 h-11 w-full border-rig-borderStrong bg-transparent font-display text-sm font-bold uppercase tracking-widest text-white hover:bg-rig-bg hover:text-rig-accent"
        >
          {joining ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Join Fleet
        </Button>
      </div>
    </div>
  );
}

/* ---------------- Driver view ---------------- */
function DriverView({ ctx, onLeft }) {
  const [confirm, setConfirm] = useState(false);
  const [dRange, setDRange] = useState("ytd");

  const leave = async () => {
    try {
      await api("/fleet/leave", { method: "POST" });
      toast.success("You left the fleet");
      onLeft();
    } catch (e) {
      toast.error(e?.message || "Could not leave");
    }
  };

  return (
    <div className="space-y-6">
      <div className="max-w-xl">
        <div className="rounded-2xl border border-rig-accent/40 bg-rig-surface p-6" data-testid="driver-fleet-card">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-rig-accent" />
            <span className="font-display text-[11px] font-bold uppercase tracking-[0.18em] text-rig-accent">Driver</span>
          </div>
          <h2 className="mt-2 font-display text-2xl font-bold text-white">{ctx.fleet.name}</h2>
          <p className="mt-2 text-sm text-rig-dim">
            You&apos;re part of this fleet. Your receipts, hauls and miles automatically roll up to your owner&apos;s dispatcher dashboard — your own app works exactly the same.
          </p>
          <Button
            onClick={() => setConfirm(true)}
            data-testid="leave-fleet-btn"
            variant="outline"
            className="mt-5 h-11 border-rig-error/60 bg-transparent text-rig-error hover:bg-rig-error/10 hover:text-rig-error"
          >
            <LogOut className="mr-2 h-4 w-4" /> Leave fleet
          </Button>
        </div>
      </div>

      {ctx.fleet?.share_compare && (
        <div>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Trophy className="h-4 w-4 text-rig-accent" />
              <p className="font-display text-xs font-bold uppercase tracking-[0.18em] text-rig-dim">Fleet Leaderboard</p>
            </div>
            <RangeToggle value={dRange} onChange={setDRange} />
          </div>
          <ComparePanel isOwner={false} range={dRange} />
        </div>
      )}

      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title="Leave this fleet?"
        description="Your data stays yours — it just stops rolling up to the owner. You can rejoin later with the code."
        confirmLabel="Leave fleet"
        destructive
        onConfirm={leave}
        testID="leave-fleet-confirm"
      />
    </div>
  );
}

/* ---------------- Owner drill-in modal ---------------- */
function DriverDetail({ driverId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api(`/fleet/drivers/${driverId}`).then(setData).catch(() => setData({ error: true }));
  }, [driverId]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto border-rig-border bg-rig-surface sm:max-w-2xl" data-testid="driver-detail-modal">
        <DialogHeader>
          <DialogTitle className="font-display tracking-wide text-white">
            {data?.driver?.name || "Driver"}
          </DialogTitle>
          <DialogDescription className="text-rig-dim">{data?.driver?.email}</DialogDescription>
        </DialogHeader>
        {!data ? (
          <Skeleton className="h-40 w-full bg-rig-surface2" />
        ) : (
          <div className="space-y-5">
            <div>
              <p className="font-display text-xs font-bold uppercase tracking-widest text-rig-dim">Trucks ({data.trucks.length})</p>
              {data.trucks.length === 0 ? (
                <p className="mt-1 text-sm text-rig-mute">No trucks assigned.</p>
              ) : (
                <div className="mt-2 flex flex-wrap gap-2">
                  {data.trucks.map((t) => (
                    <span key={t.truck_id} className="rounded-lg border border-rig-border bg-rig-bg px-3 py-1.5 text-xs text-white">
                      {t.unit_number}{t.make_model ? ` · ${t.make_model}` : ""}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div>
              <p className="font-display text-xs font-bold uppercase tracking-widest text-rig-dim">Recent Hauls</p>
              {data.hauls.length === 0 ? (
                <p className="mt-1 text-sm text-rig-mute">No hauls logged.</p>
              ) : (
                <div className="mt-2 space-y-2">
                  {data.hauls.slice(0, 6).map((h) => (
                    <div key={h.id} className="flex items-center justify-between rounded-lg border border-rig-border bg-rig-bg px-3 py-2 text-sm">
                      <span className="text-white">{h.pickup_city || "—"} → {h.delivery_city || "—"}</span>
                      <span className="text-rig-accent">{fmtMoney(h.rate_usd || 0)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <p className="font-display text-xs font-bold uppercase tracking-widest text-rig-dim">Recent Receipts</p>
              {data.receipts.length === 0 ? (
                <p className="mt-1 text-sm text-rig-mute">No receipts logged.</p>
              ) : (
                <div className="mt-2 space-y-2">
                  {data.receipts.slice(0, 8).map((r) => (
                    <div key={r.id} className="flex items-center justify-between rounded-lg border border-rig-border bg-rig-bg px-3 py-2 text-sm">
                      <span className="text-white">{r.vendor} · {r.category}</span>
                      <span className="text-rig-dim">{fmtMoney(r.amount || 0)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Owner view ---------------- */
const OWNER_TABS = [
  { key: "dispatch", label: "Dispatch" },
  { key: "trucks", label: "Trucks" },
  { key: "compare", label: "Leaderboard" },
];

function OwnerView({ ctx, overview, reload, range, setRange }) {
  const [tab, setTab] = useState("dispatch");
  const [addDriver, setAddDriver] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [tempCred, setTempCred] = useState(null); // {email, temp_password}

  // Add-driver form
  const [dEmail, setDEmail] = useState("");
  const [dName, setDName] = useState("");
  const [dBusy, setDBusy] = useState(false);

  const code = overview?.fleet?.join_code || ctx?.fleet?.join_code || "";

  const copyCode = () => {
    navigator.clipboard?.writeText(code);
    toast.success("Join code copied");
  };

  // One-tap driver invites — pre-filled email / text with the join code.
  const fleetName = overview?.fleet?.name || ctx?.fleet?.name || "my fleet";
  const appUrl = typeof window !== "undefined" ? window.location.origin : "";
  const inviteText = `Join my RigBooks fleet "${fleetName}". Download RigBooks and enter join code ${code} to link up.${appUrl ? ` ${appUrl}` : ""}`;
  const emailHref = `mailto:?subject=${encodeURIComponent("Join my RigBooks fleet")}&body=${encodeURIComponent(inviteText)}`;
  const smsHref = `sms:?&body=${encodeURIComponent(inviteText)}`;
  const canShare = typeof navigator !== "undefined" && !!navigator.share;
  const shareInvite = async () => {
    if (canShare) {
      try { await navigator.share({ title: "RigBooks fleet invite", text: inviteText }); } catch (e) { console.debug("Share sheet dismissed:", e); }
    }
  };

  const submitDriver = async () => {
    setDBusy(true);
    try {
      const r = await api("/fleet/drivers", { method: "POST", body: { email: dEmail, name: dName } });
      setAddDriver(false);
      setDEmail(""); setDName("");
      setTempCred(r);
      reload();
    } catch (e) {
      toast.error(e?.message || "Could not add driver");
    } finally {
      setDBusy(false);
    }
  };

  const removeDriver = async () => {
    try {
      await api(`/fleet/drivers/${removeTarget.user_id}`, { method: "DELETE" });
      toast.success("Driver removed");
      reload();
    } catch (e) {
      toast.error(e?.message || "Could not remove");
    }
  };

  const exportCsv = async () => {
    try {
      await downloadPdf(`${BACKEND_BASE}/api/fleet/export-csv?range=${range}`, `rigbooks_fleet_${range}.csv`);
    } catch {
      toast.error("Export failed");
    }
  };

  const totals = overview?.totals || {};
  const drivers = overview?.drivers || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-rig-border bg-rig-surface p-5">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Crown className="h-4 w-4 text-rig-accent" />
            <span className="font-display text-[10px] font-bold uppercase tracking-[0.18em] text-rig-accent">Owner</span>
          </div>
          <h2 className="mt-1 truncate font-display text-2xl font-bold text-white" data-testid="fleet-name">
            {overview?.fleet?.name || ctx.fleet.name}
          </h2>
        </div>
        <button
          onClick={copyCode}
          data-testid="copy-join-code"
          className="flex items-center gap-2 rounded-xl border border-rig-borderStrong bg-rig-bg px-4 py-2.5 transition-colors hover:border-rig-accent"
        >
          <span className="text-[10px] font-bold uppercase tracking-widest text-rig-dim">Join code</span>
          <span className="font-display text-base font-bold tracking-widest text-rig-accent">{code}</span>
          <Copy className="h-4 w-4 text-rig-dim" />
        </button>
        <div className="flex flex-wrap gap-2" data-testid="invite-actions">
          <a
            href={emailHref}
            data-testid="invite-email"
            className="flex items-center gap-1.5 rounded-xl border border-rig-borderStrong bg-rig-bg px-3.5 py-2.5 text-xs font-semibold text-white transition-colors hover:border-rig-accent hover:text-rig-accent"
          >
            <Mail className="h-4 w-4" /> Email invite
          </a>
          <a
            href={smsHref}
            data-testid="invite-text"
            className="flex items-center gap-1.5 rounded-xl border border-rig-borderStrong bg-rig-bg px-3.5 py-2.5 text-xs font-semibold text-white transition-colors hover:border-rig-accent hover:text-rig-accent"
          >
            <MessageSquare className="h-4 w-4" /> Text invite
          </a>
          {canShare && (
            <button
              onClick={shareInvite}
              data-testid="invite-share"
              className="flex items-center gap-1.5 rounded-xl border border-rig-borderStrong bg-rig-bg px-3.5 py-2.5 text-xs font-semibold text-white transition-colors hover:border-rig-accent hover:text-rig-accent"
            >
              <Send className="h-4 w-4" /> Share
            </button>
          )}
        </div>
        <div className="ml-auto">
          <Button onClick={() => setAddDriver(true)} data-testid="add-driver-btn" className="h-10 bg-rig-accent font-display text-xs font-bold uppercase tracking-widest text-rig-accentText hover:bg-rig-accentDark">
            <Plus className="mr-1 h-4 w-4" /> Add Driver
          </Button>
        </div>
      </div>

      {/* Compliance alerts */}
      {overview?.compliance && (overview.compliance.expired > 0 || overview.compliance.expiring_soon > 0) && (
        <div className="flex items-center gap-3 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4" data-testid="compliance-banner">
          <AlertTriangle className="h-5 w-5 shrink-0 text-amber-400" />
          <div className="text-sm text-white">
            <span className="font-display font-bold text-amber-400">Compliance alerts:</span>{" "}
            {overview.compliance.expired > 0 && <span>{overview.compliance.expired} document{overview.compliance.expired > 1 ? "s" : ""} expired</span>}
            {overview.compliance.expired > 0 && overview.compliance.expiring_soon > 0 && <span> · </span>}
            {overview.compliance.expiring_soon > 0 && <span>{overview.compliance.expiring_soon} expiring within 30 days</span>}
            <span className="text-rig-dim"> — see the Trucks tab.</span>
          </div>
        </div>
      )}

      {/* Tabs + range + export */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-rig-border">
        <div className="flex gap-1">
          {OWNER_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`fleet-tab-${t.key}`}
              className={`relative px-4 py-2.5 font-display text-xs font-bold uppercase tracking-widest transition-colors ${tab === t.key ? "text-rig-accent" : "text-rig-dim hover:text-white"}`}
            >
              {t.label}
              {tab === t.key && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-rig-accent" />}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 pb-2">
          <RangeToggle value={range} onChange={setRange} />
          <Button onClick={exportCsv} data-testid="fleet-export-csv" variant="outline" className="h-9 border-rig-borderStrong bg-transparent text-xs text-white hover:bg-rig-bg hover:text-rig-accent">
            <FileDown className="mr-1 h-4 w-4" /> Export CSV
          </Button>
        </div>
      </div>

      {tab === "dispatch" && (
        <div className="space-y-6">
          {/* Fleet totals */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatBox icon={DollarSign} label="YTD Spend" value={fmtMoney(totals.ytd_spend || 0)} />
            <StatBox icon={Package} label="Haul Gross" value={fmtMoney(totals.haul_gross || 0)} accent />
            <StatBox icon={Route} label="Total Miles" value={(totals.miles || 0).toLocaleString()} />
            <StatBox icon={Truck} label="Active Hauls" value={totals.active_hauls || 0} />
          </div>

          {/* Driver rollup cards */}
          <div>
            <p className="mb-3 font-display text-xs font-bold uppercase tracking-[0.18em] text-rig-dim">
              Drivers ({overview?.driver_count || drivers.length})
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              {drivers.map((d) => (
                <div key={d.user_id} className="rounded-2xl border border-rig-border bg-rig-surface p-5" data-testid={`driver-card-${d.user_id}`}>
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-display text-base font-bold text-white">{d.name}</span>
                        {d.is_owner && <ShieldCheck className="h-4 w-4 shrink-0 text-rig-accent" />}
                      </div>
                      <p className="truncate text-xs text-rig-dim">{d.email}</p>
                    </div>
                    <span className="rounded-full border border-rig-border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-rig-dim">
                      {d.is_owner ? "Owner" : "Driver"}
                    </span>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                    <div><div className="text-rig-mute text-[11px] uppercase tracking-wider">YTD Spend</div><div className="font-display text-lg text-white">{fmtMoney(d.ytd_spend)}</div></div>
                    <div><div className="text-rig-mute text-[11px] uppercase tracking-wider">Haul Gross</div><div className="font-display text-lg text-rig-accent">{fmtMoney(d.haul_gross)}</div></div>
                    <div><div className="text-rig-mute text-[11px] uppercase tracking-wider">Miles</div><div className="font-display text-lg text-white">{(d.miles || 0).toLocaleString()}</div></div>
                    <div><div className="text-rig-mute text-[11px] uppercase tracking-wider">Active · Trucks</div><div className="font-display text-lg text-white">{d.active_hauls} · {d.truck_count}</div></div>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <Button onClick={() => setDetailId(d.user_id)} data-testid={`view-driver-${d.user_id}`} variant="outline" size="sm" className="h-9 flex-1 border-rig-borderStrong bg-transparent text-xs text-white hover:bg-rig-bg hover:text-rig-accent">
                      <Eye className="mr-1 h-4 w-4" /> View
                    </Button>
                    {!d.is_owner && (
                      <Button onClick={() => setRemoveTarget(d)} data-testid={`remove-driver-${d.user_id}`} variant="outline" size="sm" className="h-9 border-rig-error/50 bg-transparent text-xs text-rig-error hover:bg-rig-error/10 hover:text-rig-error">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "trucks" && <TrucksPanel drivers={drivers} onChanged={reload} />}
      {tab === "compare" && <ComparePanel isOwner range={range} />}

      {/* Add driver dialog */}
      <Dialog open={addDriver} onOpenChange={setAddDriver}>
        <DialogContent className="border-rig-border bg-rig-surface" data-testid="add-driver-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-wide text-white">Add a Driver</DialogTitle>
            <DialogDescription className="text-rig-dim">
              We&apos;ll create a login for them and show a one-time temporary password to hand off.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-sm text-rig-dim">Driver email</Label>
            <Input value={dEmail} onChange={(e) => setDEmail(e.target.value)} placeholder="driver@example.com" className={inputCls} data-testid="driver-email-input" />
            <Label className="mt-3 block text-sm text-rig-dim">Name (optional)</Label>
            <Input value={dName} onChange={(e) => setDName(e.target.value)} placeholder="Bob D." className={inputCls} data-testid="driver-name-input" />
          </div>
          <DialogFooter>
            <Button onClick={submitDriver} disabled={dBusy || !dEmail.trim()} data-testid="submit-driver-btn" className="h-11 w-full bg-rig-accent font-display text-sm font-bold uppercase tracking-widest text-rig-accentText hover:bg-rig-accentDark">
              {dBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Create Driver Login
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Temp password reveal */}
      <Dialog open={!!tempCred} onOpenChange={(o) => !o && setTempCred(null)}>
        <DialogContent className="border-rig-border bg-rig-surface" data-testid="temp-cred-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-wide text-white">Driver created</DialogTitle>
            <DialogDescription className="text-rig-dim">
              Share these credentials with your driver. This password is shown only once.
            </DialogDescription>
          </DialogHeader>
          {tempCred && (
            <div className="space-y-3">
              <div className="rounded-xl border border-rig-border bg-rig-bg p-4">
                <div className="text-[11px] uppercase tracking-widest text-rig-mute">Email</div>
                <div className="text-sm text-white">{tempCred.email}</div>
                <div className="mt-3 text-[11px] uppercase tracking-widest text-rig-mute">Temporary password</div>
                <div className="flex items-center justify-between">
                  <div className="font-display text-lg tracking-wide text-rig-accent" data-testid="temp-password">{tempCred.temp_password}</div>
                  <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard?.writeText(tempCred.temp_password); toast.success("Copied"); }}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {tempCred.email_sent ? (
                <div className="flex items-center gap-2 rounded-lg border border-rig-accent/40 bg-rig-accent/10 px-3 py-2 text-xs text-rig-accent" data-testid="driver-email-sent">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-rig-accent" />
                  Welcome email sent to {tempCred.email}
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-400" data-testid="driver-email-failed">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-400" />
                  Couldn&apos;t send the email — share these credentials manually.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setTempCred(null)} className="h-10 w-full bg-rig-accent font-display text-xs font-bold uppercase tracking-widest text-rig-accentText hover:bg-rig-accentDark">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {detailId && <DriverDetail driverId={detailId} onClose={() => setDetailId(null)} />}

      <ConfirmDialog
        open={!!removeTarget}
        onOpenChange={(o) => !o && setRemoveTarget(null)}
        title={`Remove ${removeTarget?.name || "driver"}?`}
        description="They'll be unlinked from your fleet. Their account and data stay intact."
        confirmLabel="Remove"
        destructive
        onConfirm={removeDriver}
        testID="remove-driver-confirm"
      />
    </div>
  );
}

/* ---------------- Redeem code (any user) ---------------- */
function RedeemCode({ onRedeemed }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const redeem = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const r = await api("/promo/redeem", {
        method: "POST",
        body: { code: code.trim(), origin_url: window.location.origin },
      });
      if (r.free) {
        toast.success("Pro unlocked! Enjoy RigBooks Pro.");
        setCode("");
        onRedeemed?.();
      } else if (r.url) {
        window.location.href = r.url;
      }
    } catch (e) {
      toast.error(e?.message || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-rig-blue/40 bg-rig-surface p-5" data-testid="redeem-code-card">
      <div className="flex items-center gap-2">
        <Ticket className="h-5 w-5 text-rig-blue" />
        <h2 className="font-display text-lg font-bold uppercase tracking-wide text-white">Redeem a Code</h2>
      </div>
      <p className="mt-2 text-sm text-rig-dim">
        Got a RigBooks Pro discount or comp code? Enter it here to unlock Pro at your special price.
      </p>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="FLEET699"
          className={`${inputCls} flex-1 font-display tracking-widest`}
          data-testid="redeem-code-input"
        />
        <Button
          onClick={redeem}
          disabled={busy || !code.trim()}
          data-testid="redeem-code-btn"
          className="h-11 bg-rig-blue font-display text-sm font-bold uppercase tracking-widest text-white hover:opacity-90 sm:w-40"
        >
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Redeem
        </Button>
      </div>
    </div>
  );
}

/* ---------------- Page ---------------- */
export default function Fleet() {
  const { refresh: refreshAuth } = useAuth();
  const { isPro } = useProStatus();
  const [ctx, setCtx] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("ytd");

  const load = useCallback(async () => {
    try {
      const c = await api("/fleet");
      setCtx(c);
      if (c.is_owner) {
        const o = await api(`/fleet/overview?range=${range}`);
        setOverview(o);
      } else {
        setOverview(null);
      }
    } catch {
      setCtx({ in_fleet: false });
    } finally {
      setLoading(false);
    }
  }, [range]);

  const reloadAll = useCallback(async () => {
    await load();
    refreshAuth();
  }, [load, refreshAuth]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="mb-6 flex items-center gap-3">
        <Users className="h-7 w-7 text-rig-accent" />
        <div>
          <h1 className="font-display text-2xl font-bold uppercase tracking-wide text-white">Fleet</h1>
          <p className="text-sm text-rig-dim">Track multiple trucks and drivers from one dashboard.</p>
        </div>
      </div>

      {loading || !ctx ? (
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-56 w-full bg-rig-surface2" />
          <Skeleton className="h-56 w-full bg-rig-surface2" />
        </div>
      ) : !ctx.in_fleet ? (
        <NoFleet isPro={isPro} onCreated={reloadAll} onJoined={reloadAll} />
      ) : ctx.is_owner ? (
        <OwnerView ctx={ctx} overview={overview} reload={load} range={range} setRange={setRange} />
      ) : (
        <DriverView ctx={ctx} onLeft={reloadAll} />
      )}

      <div className="mt-6 max-w-3xl">
        <RedeemCode onRedeemed={reloadAll} />
      </div>
    </div>
  );
}
